import { useEffect, useState } from 'react';

const USER = 'nelitow';

const ACTION_LABELS = {
  PushEvent: 'Pushed',
  PullRequestEvent: 'Pull request',
  PullRequestReviewEvent: 'Reviewed',
  PullRequestReviewCommentEvent: 'Commented',
  IssueCommentEvent: 'Commented',
  IssuesEvent: 'Issue',
  CreateEvent: 'Created',
  DeleteEvent: 'Deleted',
  WatchEvent: 'Starred',
  ForkEvent: 'Forked',
};

function relativeDate(iso) {
  const days = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return '1d ago';
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

/**
 * GitHub's public REST API — no token, 60 req/hr per visitor IP.
 * /events/public only retains ~90 days, so an empty feed is a normal
 * state rather than an error; callers must render an empty message.
 */
export function useGitHub() {
  const [data, setData] = useState({
    loading: true,
    profile: null,
    events: [],
    years: [],
    cumulative: [],
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    Promise.all([
      fetch(`https://api.github.com/users/${USER}`).then(r => r.json()),
      fetch(`https://api.github.com/users/${USER}/events/public?per_page=100`).then(r => r.json()),
      fetch(`https://api.github.com/users/${USER}/repos?per_page=100`).then(r => r.json()),
    ])
      .then(([profile, events, repos]) => {
        if (cancelled) return;
        if (!Array.isArray(events)) events = [];
        if (!Array.isArray(repos)) repos = [];

        const rows = events.slice(0, 8).map((e, i) => ({
          id: `${e.id || i}`,
          action: ACTION_LABELS[e.type] || e.type.replace('Event', ''),
          repo: e.repo.name,
          when: relativeDate(e.created_at),
          url: `https://github.com/${e.repo.name}`,
        }));

        const activeRepos = new Set(events.map(e => e.repo.name)).size;

        // Cumulative public repos, bucketed by month from the first repo to now.
        // Chart floors its resolution at 24 points and pads short series by
        // repeating the last value, so a coarse per-year series renders as a flat
        // tail — monthly buckets keep the point count well above that floor.
        const created = repos
          .map(r => new Date(r.created_at))
          .sort((a, b) => a - b);
        const now = new Date();
        const months = [];
        const cumulative = [];
        if (created.length) {
          const cursor = new Date(created[0].getFullYear(), created[0].getMonth(), 1);
          while (cursor <= now) {
            const end = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
            months.push(new Date(cursor));
            cumulative.push(created.filter(d => d < end).length);
            cursor.setMonth(cursor.getMonth() + 1);
          }
        }

        // Year ticks for the axis, evenly spaced across the monthly series.
        const years = [];
        months.forEach(m => {
          if (m.getMonth() === 0 || years.length === 0) {
            const y = m.getFullYear();
            if (!years.includes(y)) years.push(y);
          }
        });

        setData({
          loading: false,
          error: null,
          profile: {
            repos: profile.public_repos ?? 0,
            followers: profile.followers ?? 0,
            years: profile.created_at
              ? new Date().getFullYear() - new Date(profile.created_at).getFullYear()
              : 0,
            location: profile.location || '—',
          },
          events: rows,
          activeRepos,
          years,
          cumulative,
        });
      })
      .catch(err => {
        if (!cancelled)
          setData({
            loading: false,
            profile: null,
            events: [],
            years: [],
            cumulative: [],
            error: err,
          });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}
